import type { ISocialLinks } from '../redux/buzzbridge/types';
import type { IBuzzBridgeConfig } from '../types/global/helper';

const PLATFORM_AUTO_LINK_KEY: Record<string, keyof NonNullable<IBuzzBridgeConfig['enableAutoLink']>> = {
    instagram: 'Instagram',
    tiktok: 'TikTok',
    youtube: 'YouTube',
};

export const CREATOR_AUTO_LINK_PLATFORM_IDS = ['instagram', 'tiktok', 'youtube'] as const;
export type CreatorAutoLinkPlatformId = (typeof CREATOR_AUTO_LINK_PLATFORM_IDS)[number];

const PLATFORM_LABEL: Record<CreatorAutoLinkPlatformId, string> = {
    instagram: 'Instagram',
    tiktok: 'TikTok',
    youtube: 'YouTube',
};

/** True when OAuth auto-link is enabled for this brand platform (default: enabled). */
export function isBrandPlatformAutoLinkEnabled(
    config: IBuzzBridgeConfig | undefined,
    platform: keyof ISocialLinks,
): boolean {
    const key = PLATFORM_AUTO_LINK_KEY[String(platform)];
    if (!key) return true;
    return config?.enableAutoLink?.[key] !== false;
}

/** Platforms with auto-link turned off in global buzzbridge config. */
export function getDisabledAutoLinkPlatforms(config: IBuzzBridgeConfig | undefined): CreatorAutoLinkPlatformId[] {
    return CREATOR_AUTO_LINK_PLATFORM_IDS.filter(id => !isBrandPlatformAutoLinkEnabled(config, id));
}

/**
 * Premium + sync upload require OAuth auto-link on every creator platform.
 * If any Instagram / TikTok / YouTube auto-integration is off, hosts cannot enable those modes.
 */
export function areAllPlatformAutoLinksEnabled(config: IBuzzBridgeConfig | undefined): boolean {
    return getDisabledAutoLinkPlatforms(config).length === 0;
}

export function formatDisabledAutoLinkPlatforms(config: IBuzzBridgeConfig | undefined): string {
    const disabled = getDisabledAutoLinkPlatforms(config).map(id => PLATFORM_LABEL[id]);
    if (disabled.length === 0) return '';
    if (disabled.length === 1) return disabled[0];
    if (disabled.length === 2) return `${disabled[0]} and ${disabled[1]}`;
    return `${disabled.slice(0, -1).join(', ')}, and ${disabled[disabled.length - 1]}`;
}
